import * as React from "react";
import { SelectEditor } from "../../../../../../../react-core/Html/SelectEditor";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { BankAccountInstruction } from "../../../../../../../Wrap/Models/Domains/Instructions/BankAccountInstruction";
import { DepositMethod } from "../../../../../../../Wrap/Constants/Enums/Domain/DepositMethod";
import { datePickerOptions, depositMethodArrays, depositMethodArraysMap, PaymentType } from "../../Resource/Contents";
import { SelectBankAccount } from "../BankAccount/SelectBankAccount";
import { SubAccountType } from "../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { BankAccount } from "../../../../../../../Wrap/Models/Domains/BankAccount/BankAccount";
import { DatePicker } from "../../../../../../../react-core/Datepicker/DatePicker";
import { addDays, addWeeks } from "../../../../../../../react-core/utils/date";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { ContentDatePicker } from "../../../../../../Shared/Entities/Constants/DateConstants";

interface IPaymentMethodsProps extends IProcessComponentProps {
    bankAccounts: BankAccount[];
    bankAccountInstructions: BankAccountInstruction[];
    payeeType: PaymentType;
    directDebitOnly?: boolean;
    excludeWrapCash?: boolean;
    excludePaymentDate?: boolean;
    subAccountType: SubAccountType;
    onSaveNewBankAccountInstruction?: (BankAccountInstruction: BankAccountInstruction) => void;
    onRemoveBankAccountInstruction?: (BankAccountInstruction: BankAccountInstruction) => void;
    inUseBankAccountInstruction?: string[];
    customerId: number;
}

interface IPaymentMethodsState {
    addNewBankAccount: boolean;
}

export class PaymentMethods extends ProcessComponent<IPaymentMethodsProps, IPaymentMethodsState> {
    constructor(props: any) {
        super(props);
        this.state = { addNewBankAccount: false };
    }

    componentDidMount() {
        super.componentDidMount();
        const { data, onChange } = this.props;
        if (data?.PaymentMethod == DepositMethod.NotSpecified) {
            onChange({ PaymentMethod: DepositMethod.DirectCredit });
        }
    }

    private getPaymentMethods = () => {
        return this.props.payeeType === PaymentType.Client && !this.props.excludeWrapCash
            ? depositMethodArrays
            : depositMethodArrays.filter((u) => u != DepositMethod.WrapCash);
    };

    private getFirstPaymentDate = () => {
        const curDate = new Date();
        const daysToAdd =
            curDate.getDay() == 0
                ? 12 //sunday = 0
                : curDate.getDay() == 6
                ? 13 //saturday = 6
                : 14;
        return addDays(curDate, daysToAdd);
    };

    private renderBankAccount() {
        const {
            payeeType,
            data,
            onChange,
            bankAccountInstructions,
            bankAccounts,
            excludePaymentDate,
            onSaveNewBankAccountInstruction,
            onRemoveBankAccountInstruction,
            inUseBankAccountInstruction,
            customerId,
        } = this.props;
        return (
            <>
                <div className="col-12">
                    <SelectBankAccount
                        payeeType={payeeType}
                        bankAccountInstructions={bankAccountInstructions}
                        bankAccounts={bankAccounts}
                        data={data}
                        onChange={(c) => onChange(c)}
                        onRef={(ref) => this.appendChild(ref)}
                        showErrorMsgPopUp={this.props.showErrorMsgPopUp}
                        isReview={this.props.isReview}
                        onSaveNewBankAccountInstruction={onSaveNewBankAccountInstruction}
                        onRemoveBankAccountInstruction={onRemoveBankAccountInstruction}
                        inUseBankAccountInstruction={inUseBankAccountInstruction}
                        customerId={customerId}
                    />

                    {!excludePaymentDate && this.renderPaymentDate(data, onChange)}
                </div>
            </>
        );
    }

    private renderPaymentDate(data: any, onChange) {
        return (
            <div className="row">
                <div className="col-md-4 col-12">
                    <DatePicker
                        label="Payment date"
                        selected={JsonDateParser.toDateNoOffset(data?.PaymentDate)}
                        onChange={(v) => onChange({ PaymentDate: JsonDateParser.toJsonDateNoOffset(v) })}
                        atLeast={{
                            value: this.getFirstPaymentDate(),
                            errorMessage: "Date must be at least 10 working days from today",
                        }}
                        atMost={{
                            value: addWeeks(new Date(), 6),
                            errorMessage:
                                "The Maximum period over which single Direct Debits can be loaded is six weeks.",
                        }}
                        filterDate={{
                            validator: (v) => v.getDay() !== 0 && v.getDay() !== 6,
                            errorMessage: "Payment date cannot fall on a weekend",
                        }}
                        alwaysValidateOnUpdate={false}
                        {...datePickerOptions}
                        required="Please select Date"
                    />
                </div>
            </div>
        );
    }

    public render() {
        const { data, onChange } = this.props;
        const paymentMenthods = this.getPaymentMethods();

        return (
            <>
                <div className="col-md-4 col-12">
                    <SelectEditor
                        options={this.props.directDebitOnly ? [DepositMethod.DirectDebit] : paymentMenthods}
                        getText={(v) => depositMethodArraysMap[v]}
                        required="Please select Payment method"
                        selected={data?.PaymentMethod}
                        label="Payment method"
                        onChange={(v) => onChange({ PaymentMethod: v, BankAccountNumber: null })}
                    />
                </div>
                {data?.PaymentMethod === DepositMethod.DirectDebit && this.renderBankAccount()}
            </>
        );
    }
}
